import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import InfluencerTable from './InfluencerTable';
import {
  fetchInfluencerDetail,
  fetchInfluencers,
  resolveInfluencerPayload,
  resolveInfluencerRows,
} from '../../_services/influencers/influencer.service';
import './influencers.css';

const normalizePlatform = (row = {}) => {
  const rawValue = String(
    row?.platform || row?.accountType || row?.AccountType || row?.platform_name || ''
  ).toLowerCase();

  if (rawValue.includes('youtube')) return 'youtube';
  if (rawValue.includes('facebook') || rawValue === 'page') return 'facebook';
  return 'instagram';
};

const pickValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const normalizeInfluencerRow = (row = {}) => ({
  id: String(row?.id || row?.handler_id || row?.handlerunique_id || ''),
  handle:
    row?.instagram_username ||
    row?.instagram_account ||
    row?.ProfileID ||
    row?.username ||
    row?.handle ||
    'Not available',
  avatar: row?.profile_picture_url || row?.profile_image || row?.profile_pic || '',
  bioSnippet: row?.bio || row?.Biography || row?.biography || '',
  platform: normalizePlatform(row),
  profileUrl: row?.profile_url || row?.ProfileURL || row?.profileUrl || '-',
  followers: pickValue(
    row?.followers_count,
    row?.FollowersCount,
    row?.follower_count,
    row?.followers
  ),
  reach: undefined,
  profileViews: undefined,
});

const resolveDetailObject = (payload) => {
  if (!payload) return {};
  if (Array.isArray(payload)) return payload[0] || {};
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  return payload;
};

const withAccountAnalytics = async (baseRows) => {
  const responses = await Promise.allSettled(baseRows.map((row) => fetchInfluencerDetail(row.id)));

  return baseRows.map((row, index) => {
    const response = responses[index];
    if (response.status !== 'fulfilled') return row;

    const detail = resolveDetailObject(resolveInfluencerPayload(response.value));
    const analytics = detail?.account_analytics || {};

    return {
      ...row,
      reach: analytics?.reach,
      profileViews: analytics?.profile_views,
      followers: pickValue(analytics?.follower_count, row.followers),
    };
  });
};

const InfluencerListPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadInfluencers = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetchInfluencers();

        if ((response?.status || 500) >= 400) {
          throw new Error('Unable to load influencers.');
        }

        if (!isMounted) return;
        const payload = resolveInfluencerPayload(response);
        const baseRows = resolveInfluencerRows(payload)
          .map(normalizeInfluencerRow)
          .filter((row) => row.id);
        setRows(baseRows);

        const enrichedRows = await withAccountAnalytics(baseRows);
        if (!isMounted) return;
        setRows(enrichedRows);
      } catch (fetchError) {
        if (!isMounted) return;
        setError('Failed to load influencers. Please try again.');
        setRows([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadInfluencers();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasRows = useMemo(() => rows.length > 0, [rows]);

  return (
    <Box className="influencer-page-shell">
      <Box className="influencer-page-header">
        <Box>
          <Typography variant="h4" className="influencer-page-title">
            Influencers
          </Typography>
          <Typography variant="body2" className="influencer-page-subtitle">
            Review creator profiles, platform details, and headline performance in one table.
          </Typography>
        </Box>
        {!loading ? <Chip label={`${rows.length} Influencer${rows.length === 1 ? '' : 's'}`} className="influencer-count-chip" /> : null}
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      <Box className="influencer-table-shell">
        <InfluencerTable
          rows={rows}
          loading={loading}
          onRowClick={(row) => history.push(`/influencers/${row.id}`)}
        />

        {!loading && !error && !hasRows ? (
          <Box className="influencer-empty-state">
            <Typography variant="h6">No influencers found</Typography>
            <Typography variant="body2">
              The public influencer list is empty right now.
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default InfluencerListPage;
